import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2, Trash2, Upload, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import aiService from '../../services/aiService';
import uploadService from '../../services/uploadService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName?: string;
}

const DOC_CATEGORIES = [
  {
    category: 'Income & Cash Flow',
    items: [
      { key: 'taxReturns', label: 'Tax Returns (Past 2-3 Years)', domain: 'retirement-tax' },
      { key: 'payStubs', label: 'Pay Stubs (Recent 1-2 Months)', domain: 'financial-planning' },
      { key: 'socialSecurity', label: 'Social Security Benefit Statements', domain: 'retirement-tax' },
      { key: 'budget', label: 'Annual Budget / Expense Summary', domain: 'financial-planning' },
    ],
  },
  {
    category: 'Assets & Investments',
    items: [
      { key: 'bankStatements', label: 'Bank Statements (Checking/Savings/CDs)', domain: 'investment' },
      { key: 'brokerageStatements', label: 'Brokerage Account Statements (Taxable)', domain: 'investment' },
      { key: 'retirementStatements', label: 'Retirement Account Statements (401k/IRA)', domain: 'retirement-tax' },
      { key: 'pensionEstimates', label: 'Pension Estimate Summaries', domain: 'retirement-tax' },
      { key: 'realEstate', label: 'Real Estate Deeds & Valuations', domain: 'estate-risk' },
    ],
  },
  {
    category: 'Liabilities & Debt',
    items: [
      { key: 'mortgage', label: 'Mortgage Statements', domain: 'investment' },
      { key: 'studentLoans', label: 'Student Loan Statements', domain: 'investment' },
      { key: 'personalLoans', label: 'Personal Loans & Credit Cards', domain: 'investment' },
    ],
  },
  {
    category: 'Insurance & Risk',
    items: [
      { key: 'lifeInsurance', label: 'Life Insurance Policies', domain: 'estate-risk' },
      { key: 'disabilityLtc', label: 'Disability & Long-Term Care Policies', domain: 'estate-risk' },
      { key: 'annuities', label: 'Annuity Contracts & Riders', domain: 'estate-risk' },
    ],
  },
  {
    category: 'Legal & Estate',
    items: [
      { key: 'wills', label: 'Wills & Healthcare Proxies', domain: 'estate-risk' },
      { key: 'trusts', label: 'Trust Agreements', domain: 'estate-risk' },
      { key: 'businessEntity', label: 'Business Entity Documents (P&L, Cap Tables)', domain: 'general' },
    ],
  },
];

const DEBT_TYPES = ['Mortgage', 'Auto Loan', 'Student Loan', 'Personal Loan', 'Credit Card', 'HELOC', 'Business Loan'];
const GOAL_TYPES = ['Home Purchase', 'Retirement', 'Education / College', 'Emergency Fund', 'Vacation / Travel', 'Car', 'Business', 'Start a Family'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border bg-card px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
      />
    </label>
  );
}

export default function FinancialIntakeDialog({ open, onOpenChange, clientId, clientName }: Props) {
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, { id: string; name: string }[]>>({});
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const num = (path: string) => {
    const val = path.split('.').reduce((acc: any, k: string) => (acc == null ? undefined : acc[k]), form);
    return val == null ? '' : String(val);
  };
  const setPath = (path: string, value: any) => {
    setForm((prev: any) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur: any = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] == null ? {} : { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setNum = (path: string, value: string) => setPath(path, value === '' ? undefined : Number(value));

  const refreshDocs = async () => {
    if (!clientId) return;
    try {
      const docsResp: any = await aiService.getCorpusDocuments(clientId);
      const docs = docsResp.data || docsResp || [];

      // Fallback mapping: if a document has no documentType, assign it to the
      // intake category whose corpus domain matches the document's domain.
      const domainToKey: Record<string, string> = {};
      DOC_CATEGORIES.forEach((cat) =>
        cat.items.forEach((item) => {
          if (!(item.domain in domainToKey)) domainToKey[item.domain] = item.key;
        }),
      );

      const grouped: Record<string, { id: string; name: string }[]> = {};
      docs.forEach((d: any) => {
        let key = d.documentType;
        if (!key && d.domain) key = domainToKey[d.domain];
        if (!key) return;
        const name = d.sourceTitle || d.sourceId || 'document';
        grouped[key] = [...(grouped[key] || []), { id: d.id, name }];
      });
      setUploadedDocs(grouped);
    } catch (e) {
      console.error('Error loading uploaded documents:', e);
    }
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      const resp: any = await aiService.getCorpusDocumentDownloadUrl(documentId);
      const url = resp?.url || resp?.data?.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Could not open document');
      }
    } catch (e: any) {
      console.error('Error opening document:', e);
      toast.error(e?.message || 'Could not open document');
    }
  };

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const resp: any = await aiService.getFinancialIntake(clientId);
      const data = resp.data || resp || {};
      setForm(data);
    } catch (e) {
      console.error('Error loading intake:', e);
    } finally {
      setLoading(false);
    }

    // Load uploaded documents per category (persisted in the corpus).
    await refreshDocs();
  };

  useEffect(() => {
    if (open) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const save = async () => {
    setSaving(true);
    try {
      await aiService.saveFinancialIntake(clientId, form);
      toast.success('Financial intake saved');
    } catch (e: any) {
      console.error('Error saving intake:', e);
      toast.error(e?.message || 'Failed to save intake');
    } finally {
      setSaving(false);
    }
  };

  const loadSampleData = async () => {
    try {
      const resp: any = await aiService.getSampleIntake();
      const data = resp.data || resp || {};
      setForm(data);
      toast.success('Sample data loaded');
    } catch (e: any) {
      console.error('Error loading sample data:', e);
      toast.error(e?.message || 'Failed to load sample data');
    }
  };

  const downloadSampleDocs = async () => {
    try {
      await aiService.downloadSampleDocuments();
      toast.success('Sample documents downloaded');
    } catch (e: any) {
      console.error('Error downloading sample documents:', e);
      toast.error(e?.message || 'Failed to download sample documents');
    }
  };

  const addDebt = () => {
    const list = [...(form.debts || [])];
    list.push({ type: '', balance: 0, interestRatePct: 0, minPayment: 0 });
    setForm({ ...form, debts: list });
  };
  const updateDebt = (idx: number, field: string, value: string) => {
    const list = [...(form.debts || [])];
    list[idx][field] = field === 'type' ? value : Number(value || 0);
    setForm({ ...form, debts: list });
  };
  const removeDebt = (idx: number) => {
    setForm({ ...form, debts: (form.debts || []).filter((_: any, i: number) => i !== idx) });
  };

  const addGoal = () => {
    const list = [...(form.goals || [])];
    list.push({ name: '', targetAmount: 0, targetDate: '', currentSavings: 0, priority: 'medium' });
    setForm({ ...form, goals: list });
  };
  const updateGoal = (idx: number, field: string, value: string) => {
    const list = [...(form.goals || [])];
    if (field === 'targetAmount' || field === 'currentSavings') {
      list[idx][field] = Number(value || 0);
    } else {
      list[idx][field] = value;
    }
    setForm({ ...form, goals: list });
  };
  const removeGoal = (idx: number) => {
    setForm({ ...form, goals: (form.goals || []).filter((_: any, i: number) => i !== idx) });
  };

  const uploadDoc = async (item: { key: string; label: string; domain: string }, file: File | null) => {
    if (!file) return;
    setUploading(item.key);
    try {
      // 1. Upload directly to S3 via the existing presigned-upload flow
      const { key } = await uploadService.uploadFile(file, 'financialplanning');
      // 2. Ingest the S3 object into the agent corpus (extract text + chunk)
      await aiService.ingestCorpusS3({
        domain: item.domain,
        sourceTitle: file.name,
        sourceId: key,
        documentType: item.key,
        clientId: clientId || undefined,
        isPublic: false,
      });
      await refreshDocs();
      toast.success(`Uploaded ${file.name} to S3`);
    } catch (e: any) {
      console.error('Upload error:', e);
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">
            Client Financial Intake{clientName ? ` — ${clientName}` : ''}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 text-sm">
            {/* Sample toolbar */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
              <span className="text-xs font-medium text-muted-foreground">Test with sample data:</span>
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={loadSampleData}>
                <FileText className="h-3 w-3 mr-1" /> Load sample data
              </Button>
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={downloadSampleDocs}>
                <Download className="h-3 w-3 mr-1" /> Download sample documents
              </Button>
            </div>

            {/* Budget & Cash Flow */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Investment Assets & Budget</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <NumberField label="Gross monthly income" value={num('cashFlow.grossMonthlyIncome')} onChange={(v) => setNum('cashFlow.grossMonthlyIncome', v)} />
                <NumberField label="Net monthly income" value={num('cashFlow.netMonthlyIncome')} onChange={(v) => setNum('cashFlow.netMonthlyIncome', v)} />
                <NumberField label="Fixed monthly expenses" value={num('cashFlow.fixedMonthlyExpenses')} onChange={(v) => setNum('cashFlow.fixedMonthlyExpenses', v)} />
                <NumberField label="Variable monthly expenses" value={num('cashFlow.variableMonthlyExpenses')} onChange={(v) => setNum('cashFlow.variableMonthlyExpenses', v)} />
                <NumberField label="Emergency fund" value={num('cashFlow.emergencyFund')} onChange={(v) => setNum('cashFlow.emergencyFund', v)} />
              </div>
            </section>

            {/* Debts & Liabilities */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase text-muted-foreground">Liabilities & Debts</h3>
                <Button type="button" variant="outline" size="sm" onClick={addDebt}>+ Add</Button>
              </div>
              {(form.debts || []).map((d: any, i: number) => {
                const isCustomDebt = !DEBT_TYPES.includes(d.type || '');
                return (
                  <div key={i} className="mb-2 grid grid-cols-2 gap-2 rounded-lg border p-2 sm:grid-cols-5">
                    <Field label="Debt type">
                      <select
                        value={isCustomDebt ? 'custom' : d.type || ''}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            updateDebt(i, 'type', d.type || '');
                          } else {
                            updateDebt(i, 'type', e.target.value);
                          }
                        }}
                        className="rounded border bg-card px-2 py-1 text-xs outline-none"
                      >
                        <option value="">Select…</option>
                        {DEBT_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value="custom">Custom…</option>
                      </select>
                      {isCustomDebt && (
                        <input
                          value={d.type || ''}
                          onChange={(e) => updateDebt(i, 'type', e.target.value)}
                          placeholder="Enter custom debt type…"
                          className="rounded border bg-card px-2 py-1 text-xs outline-none"
                        />
                      )}
                    </Field>
                    <Field label="Balance ($)">
                      <input type="number" value={d.balance || ''} onChange={(e) => updateDebt(i, 'balance', e.target.value)} placeholder="0.00" className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <Field label="Interest rate (%)">
                      <input type="number" value={d.interestRatePct || ''} onChange={(e) => updateDebt(i, 'interestRatePct', e.target.value)} placeholder="0.0" className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <Field label="Min payment ($/mo)">
                      <input type="number" value={d.minPayment || ''} onChange={(e) => updateDebt(i, 'minPayment', e.target.value)} placeholder="0.00" className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <div className="flex items-end">
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" onClick={() => removeDebt(i)} title="Remove"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </section>

            {/* Goals */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase text-muted-foreground">Goals</h3>
                <Button type="button" variant="outline" size="sm" onClick={addGoal}>+ Add</Button>
              </div>
              {(form.goals || []).map((g: any, i: number) => {
                const isCustomGoal = !GOAL_TYPES.includes(g.name || '');
                return (
                  <div key={i} className="mb-2 grid grid-cols-2 gap-2 rounded-lg border p-2 sm:grid-cols-6">
                    <Field label="Goal">
                      <select
                        value={isCustomGoal ? 'custom' : g.name || ''}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            updateGoal(i, 'name', g.name || '');
                          } else {
                            updateGoal(i, 'name', e.target.value);
                          }
                        }}
                        className="rounded border bg-card px-2 py-1 text-xs outline-none"
                      >
                        <option value="">Select…</option>
                        {GOAL_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value="custom">Custom…</option>
                      </select>
                      {isCustomGoal && (
                        <input
                          value={g.name || ''}
                          onChange={(e) => updateGoal(i, 'name', e.target.value)}
                          placeholder="Enter custom goal…"
                          className="rounded border bg-card px-2 py-1 text-xs outline-none"
                        />
                      )}
                    </Field>
                    <Field label="Target amount ($)">
                      <input type="number" value={g.targetAmount || ''} onChange={(e) => updateGoal(i, 'targetAmount', e.target.value)} placeholder="0.00" className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <Field label="Target date">
                      <input type="date" value={g.targetDate || ''} onChange={(e) => updateGoal(i, 'targetDate', e.target.value)} className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <Field label="Current savings ($)">
                      <input type="number" value={g.currentSavings || ''} onChange={(e) => updateGoal(i, 'currentSavings', e.target.value)} placeholder="0.00" className="rounded border bg-card px-2 py-1 text-xs outline-none" />
                    </Field>
                    <Field label="Priority">
                      <select value={g.priority || 'medium'} onChange={(e) => updateGoal(i, 'priority', e.target.value)} className="rounded border bg-card px-2 py-1 text-xs outline-none">
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </Field>
                    <div className="flex items-end">
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" onClick={() => removeGoal(i)} title="Remove"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </section>

            {/* Insurance */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Insurance & Annuities</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <NumberField label="Life insurance" value={num('insurance.life')} onChange={(v) => setNum('insurance.life', v)} />
                <NumberField label="Disability (monthly)" value={num('insurance.disabilityMonthly')} onChange={(v) => setNum('insurance.disabilityMonthly', v)} />
                <NumberField label="LTC (annual)" value={num('insurance.ltcAnnual')} onChange={(v) => setNum('insurance.ltcAnnual', v)} />
              </div>
            </section>

            {/* Executive Compensation */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Executive Compensation</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <NumberField label="Stock value" value={num('compensation.stockValue')} onChange={(v) => setNum('compensation.stockValue', v)} />
                <NumberField label="Options value" value={num('compensation.optionsValue')} onChange={(v) => setNum('compensation.optionsValue', v)} />
                <NumberField label="RSU value" value={num('compensation.rsuValue')} onChange={(v) => setNum('compensation.rsuValue', v)} />
                <NumberField label="Deferred comp" value={num('compensation.deferredComp')} onChange={(v) => setNum('compensation.deferredComp', v)} />
              </div>
            </section>

            {/* Business Ownership */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Business Ownership</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <NumberField label="Ownership %" value={num('business.ownershipPct')} onChange={(v) => setNum('business.ownershipPct', v)} />
                <NumberField label="Business value" value={num('business.value')} onChange={(v) => setNum('business.value', v)} />
                <NumberField label="Annual income" value={num('business.annualIncome')} onChange={(v) => setNum('business.annualIncome', v)} />
              </div>
            </section>

            {/* Tax Context */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Tax Context</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <NumberField label="Marginal tax rate %" value={num('tax.marginalTaxRatePct')} onChange={(v) => setNum('tax.marginalTaxRatePct', v)} />
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground">Filing status</span>
                  <select value={form.tax?.filingStatus || ''} onChange={(e) => setPath('tax.filingStatus', e.target.value || undefined)} className="rounded-lg border bg-card px-3 py-1.5 text-sm outline-none">
                    <option value="">—</option>
                    <option>Single</option>
                    <option>Married filing jointly</option>
                    <option>Married filing separately</option>
                    <option>Head of household</option>
                  </select>
                </label>
              </div>
            </section>

            {/* Document uploads */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Documents{' '}
                <span className="font-normal normal-case text-muted-foreground">
                  (Supported: PDF, TXT, MD, CSV)
                </span>
              </h3>
              <div className="space-y-3">
                {DOC_CATEGORIES.map((cat) => (
                  <div key={cat.category}>
                    <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{cat.category}</div>
                    <div className="space-y-1.5">
                      {cat.items.map((item) => (
                        <div key={item.key} className="flex items-center justify-between rounded-lg border p-2">
                          <div className="min-w-0">
                            <div className="truncate text-xs">{item.label}</div>
                            {uploadedDocs[item.key]?.map((doc) => (
                              <div key={doc.id} className="flex items-center gap-1 text-xs text-muted-foreground">
                                <FileText className="h-3 w-3 shrink-0" />
                                <button
                                  type="button"
                                  className="truncate underline underline-offset-2 hover:text-primary"
                                  title={`Open ${doc.name}`}
                                  onClick={() => handleViewDocument(doc.id)}
                                >
                                  {doc.name}
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="ml-2 flex items-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              disabled={uploading === item.key}
                              onClick={() => fileInputs.current?.[item.key]?.click()}
                            >
                              {uploading === item.key ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                              Upload
                            </Button>
                            <input
                              ref={(el) => { fileInputs.current[item.key] = el; }}
                              type="file"
                              accept=".pdf,.txt,.md,.csv"
                              className="hidden"
                              onChange={(e) => {
                                uploadDoc(item, e.target.files?.[0] || null);
                                e.target.value = '';
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          <Button size="sm" onClick={save} disabled={loading || saving}>
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Save Intake
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}